import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import CommCenterService from '@/services/CommCenterService';
import axios, { AxiosError } from 'axios';
import { MMDDYY } from '../lib/utils/date-time/utils';
import { handleApiError } from '@/utils/apiErrorHandler';
import { clearError } from './errorSlice';

export const getCounts = createAsyncThunk(
  'commCenter/getCounts',
  async (_, { rejectWithValue, dispatch }) => {
    let routeUrl = `/papi/secure/comm-center/counts`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl comm-center counts from:', routeUrl);
      const response = await axios.get(routeUrl);
      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError, "commCenter", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  }
);

export const getTicket = createAsyncThunk('commCenter/getTicket', async (
  { ticketId, lastMessageIdToShow }: { ticketId: string, lastMessageIdToShow: string }, thunkAPI) => {

  let routeUrl = `/papi/secure/comm-center/ticket?ticketId=${ticketId}`;

  if (lastMessageIdToShow) {
    routeUrl += `&lastMessageIdToShow=${lastMessageIdToShow}`;
  }

  try {
    thunkAPI?.dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl comm-center counts from:', routeUrl);
    const response = await axios.get(routeUrl);
    return response.data;
  } catch (error) {
    handleApiError(error as AxiosError, "commCenter", thunkAPI?.dispatch);
    return thunkAPI?.rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const changeTicketStatus = createAsyncThunk('commCenter/changeTicketStatus', async ({ ticketIds, archive }: { ticketIds: string[], archive: boolean }) => {
  const response = await CommCenterService.changeTicketStatus(ticketIds, archive);
  return response.data;
});

export const getNotificationsForUser = createAsyncThunk('commCenter/getNotificationsForUser',
  async ({ query }: { query: any }, { dispatch, rejectWithValue }) => {
    let routeUrl = `/papi/secure/comm-center/notifications/for-user?page=${query.page}&size=${query.size}&sort=${query.sort},${query.direction}`;
    if (query.filter) routeUrl += `&filter=${query.filter}`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl comm-center counts from:', routeUrl);
      const response = await axios.get(routeUrl);
      return { data: response?.data?.data, totalItems: response?.data?.totalItems };
    } catch (error) {
      handleApiError(error as AxiosError, "commCenter", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  });

export const getMessagesSentByUser = createAsyncThunk('commCenter/getMessagesSentByUser', async (
  { query }: { query: any }, { dispatch, rejectWithValue }) => {
  let routeUrl = `/papi/secure/comm-center/messages/sent-by-user?page=${query.page}&size=${query.size}&sort=${query.sort},${query.direction}`;
  if (query.filter) routeUrl += `&filter=${query.filter}`;

  try {
    dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl comm-center get messages from:', routeUrl);
    const response = await axios.get(routeUrl);
    return { data: response?.data?.data, totalItems: response?.data?.totalItems };
  } catch (error) {
    handleApiError(error as AxiosError, "commCenter", dispatch);
    return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

export const getTicketsForUser = createAsyncThunk('commCenter/getTicketsForUser',
  async ({ query }: { query: any }, { dispatch, rejectWithValue }) => {
    let routeUrl = `/papi/secure/comm-center/tickets-for-user?page=${query.page}&size=${query.size}&sort=${query.sort},${query.direction}`;
    if (query.filter) routeUrl += `&filter=${query.filter}`;
    try {
      dispatch(clearError());
      // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
      // The API will use the authenticated user's mpin
      console.log('routeUrl comm-center get tickets for user from:', routeUrl);
      const response = await axios.get(routeUrl);
      return { data: response?.data?.data, totalItems: response?.data?.totalItems };
    } catch (error) {
      handleApiError(error as AxiosError, "commCenter", dispatch);
      return rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
    }
  });

export const downloadNotification = createAsyncThunk('commCenter/downloadNotification', async ({ notificationId, username }: { notificationId: string, username: string }) => {
  const response = await axios.post(`/api/handler`, {
    method: 'GET',
    url: `/odm/notifications/download/${notificationId}`,
  });
  return { ...response.data };
});

export const sendMessage = createAsyncThunk('commCenter/sendMessage', async (
  { payload, query }: { payload: any; query: any }, thunkAPI) => {
  let routeUrl = `/papi/secure/comm-center/messages?origin=EXTERNAL&fromExternalId=${query.userId}&fromExternalUsername=${query.username}`;
  if (query.physicianMPIN) {
    routeUrl += `&physicianMPIN=${query.physicianMPIN}`;
  }

  try {
    thunkAPI.dispatch(clearError());
    // Fetch fetchMeasuresComplianceRules - use base endpoint (no mpin in path)
    // The API will use the authenticated user's mpin
    console.log('routeUrl comm-center send message from:', routeUrl);
    const response = await axios.post(routeUrl, payload);
    if (response.status === 201 && query.ticketId) {
      thunkAPI.dispatch(getTicket({ ticketId: query.ticketId, lastMessageIdToShow: '' }));
    }
    return { data: response?.data?.data, totalItems: response?.data?.totalItems };
  } catch (error) {
    handleApiError(error as AxiosError, "commCenter", thunkAPI.dispatch);
    return thunkAPI.rejectWithValue(error instanceof Error ? error.message : 'An error occurred');
  }
});

const initialState = {
  counts: {
    data: {
      unreadTickets: 0,
      unreadNotifications: null
    }
  },
  tickets: { data: [], totalItems: 0 },
  ticket: { data: { messages: [] } },
  messages: { data: [], totalItems: 0 },
  notifications: { data: [], totalItems: 0 },
  sendMessage: {},
  loading: false,
  error: null as string | null,
  toast: { visible: false, message: '', variant: '' },
  isComposeModalOpen: false,
};

const commCenterSlice = createSlice({
  name: 'commCenter',
  initialState,
  reducers: {
    showToast: (state, payload) => {
      state.toast.visible = payload.payload.visible || true;
      state.toast.message = payload.payload.message || 'Operation completed';
      state.toast.variant = payload.payload.variant || 'bg-info';
    },
    hideToast: (state) => {
      state.toast.visible = false;
      state.toast.message = '';
    },
    openComposeModal: (state) => {
      state.isComposeModalOpen = true;
    },
    closeComposeModal: (state) => {
      state.isComposeModalOpen = false;
    },
    toggleComposeModal: (state) => {
      state.isComposeModalOpen = !state.isComposeModalOpen;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.counts = action.payload;
      })
      .addCase(getCounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(getTicketsForUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTicketsForUser.fulfilled, (state, action) => {
        const updateData = action.payload.data.map((item: any) => {
          return {
            ...item,
            from: `${item?.fromInternalUserObject?.firstName} ${item?.fromInternalUserObject?.lastName}`,
            physicianFullName: `${item?.physicianFirstName} ${item?.physicianMiddleName} ${item?.physicianLastName}`,
            showBadge: item.unread,
            hasAttachments: { type: 'paperclip', value: item.hasAttachments },
            hrefLinks: {
              subject: `/secure/communication-center/inbox/messages/view/${item.id}`,
            }
          }
        });
        state.loading = false;
        state.tickets = {
          data: updateData,
          totalItems: action.payload.totalItems
        };
      })
      .addCase(getTicketsForUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(getTicket.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTicket.fulfilled, (state, action) => {
        state.loading = false;
        state.ticket = action.payload;
      })
      .addCase(getTicket.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(changeTicketStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeTicketStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changeTicketStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(getMessagesSentByUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMessagesSentByUser.fulfilled, (state, action) => {
        const updateData = action.payload.data?.map((item: any) => {
          return {
            ...item,
            hrefLinks: { subject: `/secure/communication-center/sent/messages/view/${item.ticketId}/${item.id}` },
            hasAttachments: { type: 'paperclip', value: item.attachments.length > 0 },
            created: MMDDYY(item.created),
          };
        })
        state.loading = false;
        state.messages = {
          data: updateData,
          totalItems: action.payload.totalItems,
        };
      })
      .addCase(getMessagesSentByUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(getNotificationsForUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotificationsForUser.fulfilled, (state, action) => {
        const updateData = action.payload.data?.map((item: any) => {
          return {
            ...item,
            hasAttachments: { type: 'pdf', value: true, attribute: 'download' },
            hrefLinks: { subject: `/papi/secure/comm-center/notifications/download?notificationId=${item.notificationID}&fileName=${item?.subject}` },
            sentDate: MMDDYY(item.sentDate),
          };
        })
        state.loading = false;
        state.notifications = {
          data: updateData,
          totalItems: action.payload.totalItems
        }
      })
      .addCase(getNotificationsForUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(downloadNotification.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadNotification.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadNotification.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
      })
      .addCase(sendMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.sendMessage = action.payload;
        state.toast.visible = true;
        state.toast.message = 'Your message has been sent.';
        state.toast.variant = 'bg-success';
        state.isComposeModalOpen = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? null;
        state.toast.visible = true;
        state.toast.message = 'There was an error submitting your message. Please try again.';
        state.toast.variant = 'bg-danger';
      });
  },
});


export const { showToast, hideToast, openComposeModal, closeComposeModal, toggleComposeModal } = commCenterSlice.actions;

export default commCenterSlice.reducer;
